// Pure helpers for the recursive WebCategory tree (max 3 levels: depth 0..2).
//
// The backend stores categories flat with an integer `parent` (0 = root) and
// enforces neither a depth limit nor cycle protection, so these helpers are
// the only guard: anything deeper than MAX_CATEGORY_DEPTH, or caught in a
// parent cycle, is promoted to a root row with a console warning rather than
// being silently dropped.

import { MAX_CATEGORY_DEPTH, type WebCategory } from '../types'

const byName = (a: WebCategory, b: WebCategory) =>
  a.displayName.localeCompare(b.displayName, undefined, {
    sensitivity: 'base',
  })

const parentKeyOf = (node: WebCategory): string | null => {
  const raw = node.parentId
  if (raw == null) return null
  const key = String(raw)
  return key === '' || key === '0' ? null : key
}

function sortTree(nodes: WebCategory[]): WebCategory[] {
  nodes.sort(byName)
  for (const n of nodes) sortTree(n.subcategories)
  return nodes
}

/**
 * Assemble a flat list of categories (each carrying its raw `parentId`) into
 * a sorted tree, assigning `depth` and normalising `parentId` to the parent's
 * string id (undefined for roots). Orphans, over-deep nodes and cycle members
 * become roots.
 */
export function assembleCategoryTree(flat: WebCategory[]): WebCategory[] {
  const nodes = new Map<string, WebCategory>()
  for (const n of flat) nodes.set(n.id, { ...n, subcategories: [] })

  const childrenOf = new Map<string, string[]>()
  const rootIds: string[] = []
  for (const n of Array.from(nodes.values())) {
    const pk = parentKeyOf(n)
    if (!pk || pk === n.id || !nodes.has(pk)) {
      rootIds.push(n.id)
      continue
    }
    const list = childrenOf.get(pk) ?? []
    list.push(n.id)
    childrenOf.set(pk, list)
  }

  const roots: WebCategory[] = []
  const visited = new Set<string>()

  const place = (id: string, depth: number, parent: WebCategory | null) => {
    const node = nodes.get(id)
    if (!node) return
    if (depth >= MAX_CATEGORY_DEPTH) {
      // biome-ignore lint/suspicious/noConsole: surface bad hierarchy data (backend has no depth guard)
      console.warn(
        `[Category] "${node.displayName}" (${id}) exceeds the ${MAX_CATEGORY_DEPTH}-level limit; showing it as a top-level row.`,
      )
      place(id, 0, null)
      return
    }
    visited.add(id)
    node.depth = depth
    node.parentId = parent ? parent.id : undefined
    // Mirror the backend's full_slug (ancestor slugs joined by "/") for rows
    // the API has not backfilled yet. Parents are placed first, so
    // `parent.fullSlug` is already resolved.
    if (!node.fullSlug) {
      node.fullSlug = parent ? `${parent.fullSlug}/${node.slug}` : node.slug
    }
    if (parent) parent.subcategories.push(node)
    else roots.push(node)
    for (const childId of childrenOf.get(id) ?? []) {
      if (!visited.has(childId)) place(childId, depth + 1, node)
    }
  }

  for (const id of rootIds) place(id, 0, null)

  // Anything not reached from a root is part of a parent cycle.
  for (const id of Array.from(nodes.keys())) {
    if (visited.has(id)) continue
    // biome-ignore lint/suspicious/noConsole: surface bad hierarchy data (backend has no cycle guard)
    console.warn(
      `[Category] "${nodes.get(id)?.displayName}" (${id}) is part of a parent cycle; showing it as a top-level row.`,
    )
    place(id, 0, null)
  }

  return sortTree(roots)
}

/** Pre-order flatten (parent before its children). */
export function flattenTree(nodes: WebCategory[]): WebCategory[] {
  const out: WebCategory[] = []
  const walk = (list: WebCategory[]) => {
    for (const n of list) {
      out.push(n)
      walk(n.subcategories)
    }
  }
  walk(nodes)
  return out
}

export function findCategory(
  nodes: WebCategory[],
  id: string,
): WebCategory | null {
  for (const n of nodes) {
    if (n.id === id) return n
    const hit = findCategory(n.subcategories, id)
    if (hit) return hit
  }
  return null
}

/** Ancestors of `id`, ordered root → direct parent. Empty for roots. */
export function getAncestors(nodes: WebCategory[], id: string): WebCategory[] {
  const walk = (
    list: WebCategory[],
    trail: WebCategory[],
  ): WebCategory[] | null => {
    for (const n of list) {
      if (n.id === id) return trail
      const hit = walk(n.subcategories, [...trail, n])
      if (hit) return hit
    }
    return null
  }
  return walk(nodes, []) ?? []
}

export function getDescendantIds(node: WebCategory): string[] {
  return flattenTree(node.subcategories).map((n) => n.id)
}

/** True when a new child of `node` would still fit inside the depth limit. */
export function canBeParent(node: Pick<WebCategory, 'depth'>): boolean {
  return node.depth < MAX_CATEGORY_DEPTH - 1
}

/** Levels below `node`: 0 for a leaf, 1 with children, 2 with grandchildren. */
export function getSubtreeHeight(node: WebCategory): number {
  return node.subcategories.reduce(
    (max, child) => Math.max(max, getSubtreeHeight(child) + 1),
    0,
  )
}

/**
 * True when `node` (with its whole subtree) can move under `parent`: the
 * parent is not the node or one of its descendants, and the deepest
 * descendant still fits inside the depth limit. The backend rejects cycles
 * but not depth, so the depth check lives here only.
 */
export function canMoveUnder(node: WebCategory, parent: WebCategory): boolean {
  if (parent.id === node.id) return false
  if (getDescendantIds(node).includes(parent.id)) return false
  return parent.depth + 1 + getSubtreeHeight(node) < MAX_CATEGORY_DEPTH
}

/** `full_slug` a child of `parentId` will get, minus its own leaf slug. */
export function getParentFullSlug(
  nodes: WebCategory[],
  parentId: string | number | undefined,
): string {
  const id = String(parentId ?? '')
  return id ? (findCategory(nodes, id)?.fullSlug ?? '') : ''
}

/** `Root › Parent › Name` label for any node in the tree. */
export function getCategoryPathLabel(nodes: WebCategory[], id: string): string {
  const node = findCategory(nodes, id)
  if (!node) return ''
  return [...getAncestors(nodes, id), node]
    .map((n) => n.displayName)
    .join(' › ')
}

export interface FilteredTree {
  tree: WebCategory[]
  /** Ids whose subtree holds a search hit — auto-expanded by the table. */
  autoExpandIds: Set<string>
}

/**
 * Filter the tree for the Web tab. `include` is a per-node gate (visible-only,
 * unmapped-only); a node that fails it hides its whole subtree. With a query,
 * a node stays when it matches, an ancestor matched, or a descendant matched;
 * nodes with a matching descendant are reported in `autoExpandIds`.
 */
export function filterCategoryTree(
  nodes: WebCategory[],
  query: string,
  include: (node: WebCategory) => boolean,
): FilteredTree {
  const q = query.trim().toLowerCase()
  const autoExpandIds = new Set<string>()

  const walk = (
    list: WebCategory[],
    ancestorMatched: boolean,
  ): { kept: WebCategory[]; hit: boolean } => {
    const kept: WebCategory[] = []
    let hit = false
    for (const n of list) {
      if (!include(n)) continue
      const selfMatch = !!q && n.displayName.toLowerCase().includes(q)
      const child = walk(n.subcategories, ancestorMatched || selfMatch)
      if (q && child.hit) autoExpandIds.add(n.id)
      if (!q || ancestorMatched || selfMatch || child.kept.length > 0) {
        kept.push({ ...n, subcategories: child.kept })
      }
      hit = hit || selfMatch || child.hit
    }
    return { kept, hit }
  }

  return { tree: walk(nodes, false).kept, autoExpandIds }
}
